import React, { useState, useEffect, FormEvent } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TextField, Input, Button } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import BackIcon from '@material-ui/icons/ArrowBack';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import api from '../../../../services/api';
import './styles.scss';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
        width: '100%',
      },
    },
    grid: {
      display: 'flex',
      '@media screen and (max-width: 991px)': {
        display: 'block',
      },
      '& > *': {
        margin: theme.spacing(1),
        width: '100%',
        '@media screen and (max-width: 991px)': {
          width: '100%',
        },
      },
    },
    button: {
      '& > *': {
        margin: theme.spacing(1),
      },
    },
  }),
);

interface ParamTypes {
  id: string;
}

function toDateInputValue(iso?: string | Date | null): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Evita deslocamento de dia por timezone ao gravar só a data (meio-dia local). */
function dateInputToIso(ymd: string): string {
  const [y, m, day] = ymd.split('-').map(Number);
  return new Date(y, m - 1, day, 12, 0, 0, 0).toISOString();
}

function BlogCadastro() {
  const [id, setId] = useState('');
  const params = useParams<ParamTypes>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [dateLocal, setDateLocal] = useState('');
  const [isNew, setIsNew] = useState(false);

  const classes = useStyles();
  const history = useHistory();

  if (!id) {
    if (params.id) {
      setId(params.id);
    } else {
      setId(uuidv4());
      setIsNew(true);
    }
  }

  useEffect(() => {
    if (!isNew && id) {
      const getData = async () => {
        const response = await api.get(`/blogs/${id}`);
        if (response.status && response.status.toString()[0] === '2') {
          const data = await response.json();
          setTitle(data.title ?? '');
          setDescription(data.description ?? '');
          setLink(data.link ?? '');
          setDateLocal(toDateInputValue(data.date));
        }
      };
      getData();
    }
  }, [id, isNew]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    try {
      let result;
      const datePayload = dateLocal ? dateInputToIso(dateLocal) : undefined;
      const trimmedDesc = description.trim();
      const trimmedLink = link.trim();
      const descriptionPayload = trimmedDesc === '' ? null : trimmedDesc;
      const linkPayload = trimmedLink === '' ? null : trimmedLink;

      if (isNew) {
        result = await api.put('/blogs', {
          id,
          title: title.trim(),
          description: descriptionPayload,
          link: linkPayload,
          enabled: true,
          ...(datePayload ? { date: datePayload } : {}),
        });
      } else {
        result = await api.patch(`/blogs/${id}`, {
          title: title.trim(),
          description: descriptionPayload,
          link: linkPayload,
          ...(datePayload ? { date: datePayload } : {}),
        });
      }

      if (result && result.status && result.status.toString()[0] === '2') {
        Swal.fire({
          title: isNew ? 'Post cadastrado com sucesso!' : 'Post atualizado com sucesso!',
          icon: 'success',
          showCloseButton: true,
          showCancelButton: true,
          focusConfirm: false,
          confirmButtonText: 'Continuar',
          cancelButtonColor: '#418107',
          cancelButtonText: 'Ver todos',
        }).then((res) => {
          if (res.isConfirmed) {
            window.location.reload();
          } else {
            history.push('/blogs');
          }
        });
      } else {
        Swal.fire({
          title: 'Não foi possível salvar. Tente novamente mais tarde',
          icon: 'error',
        });
      }
    } catch {
      Swal.fire({
        title: 'Não foi possível salvar. Tente novamente mais tarde',
        icon: 'error',
      });
    }
  }

  return (
    <main>
      <div className="cadastrar">
        <div className="grid-box grid-one">
          <div className="card">
            <div className="card-body">
              <div>
                <h1>Blog</h1>
                <form onSubmit={handleSubmit}>
                  <Input type="hidden" name="id" value={id} />
                  <div className={classes.grid}>
                    <TextField
                      label="Título"
                      variant="outlined"
                      required
                      name="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className={classes.grid}>
                    <TextField
                      label="Descrição"
                      variant="outlined"
                      name="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={3}
                    />
                  </div>
                  <div className={classes.grid}>
                    <TextField
                      label="Link"
                      variant="outlined"
                      name="link"
                      value={link}
                      onChange={(e) => setLink(e.target.value)}
                      placeholder="https://"
                    />
                  </div>
                  <div className={classes.grid}>
                    <TextField
                      label="Data"
                      variant="outlined"
                      name="date"
                      type="date"
                      value={dateLocal}
                      onChange={(e) => setDateLocal(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Opcional na criação: se vazio, usa a data atual"
                    />
                  </div>
                  <div className={classes.button}>
                    <Button variant="contained" color="primary" size="large" type="submit" startIcon={<SaveIcon />}>
                      Salvar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      startIcon={<BackIcon />}
                      onClick={() => history.push('/blogs')}
                    >
                      Voltar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default BlogCadastro;
